import * as R from 'ramda'

import { StockItemConfiguration } from '../../graphql/magento'
import { rangeStepPositive } from '../../utils/ramda'
import { Maybe } from '../../utils/types'
import {
  defaultMaxQuantityAllowedInCart,
  defaultMinQuantityAllowedInCart,
  defaultQuantityIncrement,
  maxQuantityOptionCount,
} from './config'

export const autoShipIntervalOptions: { label: string; value: string }[] = [
  {
    label: '1 month',
    value: '1',
  },
  {
    label: '2 months',
    value: '2',
  },
  {
    label: '3 months',
    value: '3',
  },
]

export interface ItemQuantityConfiguration {
  minQuantityAllowedInCart: number
  maxQuantityAllowedInCart: number
  quantityIncrement: number
}

export const coerceAtLeast =
  (atLeast: number) =>
  (value: number): number =>
    Math.max(atLeast, Number.isNaN(value) ? atLeast : value)

const coerceAtLeastOne = coerceAtLeast(1)

export const resolveItemQuantityConfig = (
  stockItemConfiguration: Maybe<
    Pick<StockItemConfiguration, 'min_sale_qty' | 'max_sale_qty' | 'qty_increments'>
  >,
): ItemQuantityConfiguration => ({
  minQuantityAllowedInCart: coerceAtLeastOne(
    Number(stockItemConfiguration?.['min_sale_qty'] || defaultMinQuantityAllowedInCart),
  ),
  maxQuantityAllowedInCart: coerceAtLeastOne(
    Number(stockItemConfiguration?.['max_sale_qty'] || defaultMaxQuantityAllowedInCart),
  ),
  quantityIncrement: coerceAtLeastOne(
    Number(stockItemConfiguration?.['qty_increments'] || defaultQuantityIncrement),
  ),
})

export const validUpdateQuantityOptionsFor =
  ({
    minQuantityAllowedInCart,
    maxQuantityAllowedInCart,
    quantityIncrement,
  }: ItemQuantityConfiguration) =>
  (maxItemOrderQuantityByUserCategory?: number): readonly number[] =>
    R.reject(
      R.gt(minQuantityAllowedInCart),
      rangeStepPositive(quantityIncrement)(
        (maxItemOrderQuantityByUserCategory &&
        maxQuantityAllowedInCart >= maxItemOrderQuantityByUserCategory
          ? maxItemOrderQuantityByUserCategory
          : maxQuantityAllowedInCart) + 1,
      )(quantityIncrement),
    )

export const validAddQuantityOptionsFor =
  ({
    minQuantityAllowedInCart,
    maxQuantityAllowedInCart,
    quantityIncrement,
  }: ItemQuantityConfiguration) =>
  ({
    curQuantity,
    maxItemOrderQuantityByUserCategory,
  }: {
    curQuantity: number
    maxItemOrderQuantityByUserCategory?: number
  }): readonly number[] =>
    R.reject(
      // reject any quantity options that would not satisfy the MOQ
      R.gt(coerceAtLeastOne(minQuantityAllowedInCart - curQuantity)),
      rangeStepPositive(
        // subtract current quantity from increment so each (option + current quantity) is a multiple
        // of quantity increment
        quantityIncrement - curQuantity,
        (maxItemOrderQuantityByUserCategory &&
        maxQuantityAllowedInCart >= maxItemOrderQuantityByUserCategory
          ? maxItemOrderQuantityByUserCategory
          : maxQuantityAllowedInCart) -
          curQuantity +
          1,
        quantityIncrement,
      ),
    )

export const takeOptionsToDisplay = R.take(maxQuantityOptionCount)

export const selectOptionsFromNumbers = R.map<number[], { label: string; value: string }[]>(
  R.pipe(R.toString, (value) => ({ label: value, value })),
)

export const selectOptionsToDisplay = R.pipe(takeOptionsToDisplay, selectOptionsFromNumbers)

export const isMaxOrMinQtyError = (errorMessage: string): boolean => {
  return (
    /The requested qty exceeds the maximum qty allowed in shopping cart/.test(errorMessage) ||
    /The fewest you may purchase is/.test(errorMessage)
  )
}

export const isQuantityError = (errorMessage: string): boolean => {
  return (
    /You cannot use decimal quantity for this product/.test(errorMessage) ||
    /You can buy this product only in quantities of/.test(errorMessage) ||
    isMaxOrMinQtyError(errorMessage)
  )
}

export const isActiveCartError = (errorMessage: string): boolean => {
  return (
    /The cart isn't active/.test(errorMessage) ||
    // Retain below error known from M2 2.3.x for backward compatibility
    /Current user does not have an active cart/.test(errorMessage)
  )
}

export const isOutOfStockError = (errorMessage: string) =>
  /There are no source items with the in stock status/.test(errorMessage) ||
  // Retain below errors known from M2 2.3.x for backward compatibility
  / Cart doesn't contain the /.test(errorMessage) ||
  /Some of the products are out of stock./.test(errorMessage) ||
  errorMessage.includes('out of stock')

export const formatQuantityError = (errorMessage: string, prefix: string): string => {
  const colonIndex = errorMessage.indexOf(':')
  const msgWithoutOriginalPrefix =
    colonIndex > -1 ? errorMessage.substring(colonIndex + 2) : errorMessage
  return `${prefix}: ${msgWithoutOriginalPrefix}`
}
